import { Inject, Injectable, makeStateKey, PLATFORM_ID, signal, TransferState } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, of, tap } from 'rxjs';

import { PromocodeData } from './pages/external-pages/feedback/feedback/models/promocode.model';
import { WindowService } from 'core/services/window.service';
import { AllowedLangsEnum } from './locale/locale.enum';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';

const PROMO_STATE_KEY = makeStateKey<PromocodeData | null>('promo-code');

@Injectable({
  providedIn: 'root'
})
export class AppService {
  set currentLangIso(iso: AllowedLangsEnum) {
    if(!iso) return;
    this.$langIso.set(iso);
    this.win.setItem('_ui_lang_iso', iso);
  }

  get currentLangIso(): AllowedLangsEnum {
    return this.$langIso();
  }

  $langIso = signal((this.win.getItem('_ui_lang_iso') || '') as AllowedLangsEnum);
  $promocode = signal<PromocodeData | null>(null);
  themeColor = '';

  constructor(
    private http: HttpClient,
    private win: WindowService,
    private transferState: TransferState,
    @Inject(PLATFORM_ID) private platformId: Object,
    @Inject(DOCUMENT) private document: Document,
  ) { }

  getPromocodes() {
    const isBrowser = isPlatformBrowser(this.platformId);
    const cached = this.transferState.get(PROMO_STATE_KEY, null);

    if (cached && isBrowser) {
      cached.cplClosed = this.win.getItem(`_cpl_${cached.code}`) === 'true';
      this.$promocode.set(cached);
      this.transferState.remove(PROMO_STATE_KEY);
      return of(cached);
    }

    return this.http.get<PromocodeData>('/get-info/promocodes').pipe(
      tap(res => {
        if (res && res.code) {
          res.cplClosed = this.win.getItem(`_cpl_${res.code}`) === 'true';
          this.$promocode.set(res);
          if (!isBrowser) {
            this.transferState.set(PROMO_STATE_KEY, res);
          }
        }
      }),
      catchError(() => of({} as PromocodeData)),
    );
  }

  closePromocodeLine() {
    const promocode = this.$promocode();
    if(!promocode) {
      return;
    }
    const lsKey = `_cpl_${this.$promocode()?.code}`;
    this.win.setItem(lsKey, 'true');
    this.$promocode.set({...promocode, cplClosed: true});
  }

  setThemeColor(color: string) {
    let meta = this.document.querySelector<HTMLMetaElement>("meta[name=theme-color]");
    if (!meta) {
      meta = this.document.createElement("meta");
      meta.name = "theme-color";
      this.document.head.appendChild(meta);
    }
    meta.content = color;
    this.themeColor = color;
  }
}
